import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdPersonAdd, MdSearch, MdPerson } from "react-icons/md";
import CampusList from "./CampusList";
import CampusFormModal from "./CampusFormModal";
import CampusAnalyticsModal from "./CampusAnalyticsModal";
import { getData, saveData, STORAGE_KEYS } from "./utils/campusUtils";
import { API_BASE_URL, getJsonAuthHeaders } from "../../../utils/api";
import { confirmDialog } from "../../../utils/dialogs";
import {
  buildPowerBiDashboardUrl,
  getPowerBiDashboardForCampus,
} from "../../../utils/powerbi";
import "./CampusListView.css";

function CampusListView({
  onSelectCampus,
  showToast,
  campuses,
  setCampuses,
  principals,
  onLogout,
  analyticsOnly = false,
}) {
  const navigate = useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [analyticsCampus, setAnalyticsCampus] = useState(null);
  const [analyticsDashboardUrl, setAnalyticsDashboardUrl] = useState("");
  const [campusSearch, setCampusSearch] = useState("");
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [campusForm, setCampusForm] = useState({
    name: "",
    location: "",
    principal: "",
  });

  const loginRole = localStorage.getItem("als_role");
  const isSuperadmin = loginRole === "superadmin";
  const isPrincipal = loginRole === "principal";
  const isAdmin = loginRole === "admin";
  const isAdministrator = loginRole === "administrator";
  const isCampusOperator = isPrincipal || isAdmin || isAdministrator;

  const handleOpenCampusDetail = (campus) => {
    const id = campus?.id ?? campus;
    if (!id) return;
    // Single click should open once — avoid double navigate.
    if (typeof onSelectCampus === "function") {
      onSelectCampus(id);
      return;
    }
    navigate(`/superadmin/campuses/${id}`);
  };

  const handleOpenCampusFees = (campus) => {
    const id = campus?.id ?? campus;
    if (!id) return;
    navigate(`/fees/campus/${id}`);
  };

  const handleOpenCampusStaff = (campus) => {
    const id = campus?.id ?? campus;
    if (!id) return;
    navigate(`/superadmin/campuses/${id}/staff`);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCampusForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateCampus = async (e) => {
    e.preventDefault();

    if (!campusForm.name) {
      showToast("error", "Error", "Campus name is required.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/campuses`, {
        method: "POST",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify({
          name: campusForm.name,
          location: campusForm.location || null,
          principal: campusForm.principal || null,
        }),
      });

      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) throw new Error("Failed to create campus");

      const createdCampus = await res.json();
      showToast(
        "success",
        "Campus Created",
        `${createdCampus.name} has been added with ID ${createdCampus.id}.`,
      );
      setShowAddModal(false);
      setCampusForm({ name: "", location: "", principal: "" });
      setCampuses((prev) => [...prev, createdCampus]);
      handleOpenCampusDetail(createdCampus);
    } catch (error) {
      console.error("Failed to create campus", error);
      showToast("error", "Create Error", "Could not create campus.");
    }
  };

  const handleDeleteCampus = async (id) => {
    if (
      !(await confirmDialog(
        "Are you sure you want to delete this campus? This will also delete all associated sessions and classes.",
        { title: "Delete Campus", confirmLabel: "Delete Campus", variant: "danger" },
      ))
    )
      return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/campuses/${id}`, {
        method: "DELETE",
        headers: getJsonAuthHeaders(),
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) throw new Error("Failed to delete campus");
    } catch (error) {
      console.error("Failed to delete campus", error);
      showToast("error", "Delete Error", "Could not delete campus.");
      return;
    }

    const allCampuses = getData(STORAGE_KEYS.CAMPUSES);
    const filteredCampuses = allCampuses.filter((c) => String(c.id) !== String(id));
    saveData(STORAGE_KEYS.CAMPUSES, filteredCampuses);

    const allSessions = getData(STORAGE_KEYS.SESSIONS);
    const filteredSessions = allSessions.filter((s) => String(s.campus_id) !== String(id));
    saveData(STORAGE_KEYS.SESSIONS, filteredSessions);

    const allClasses = getData(STORAGE_KEYS.CLASSES);
    const sessionIds = allSessions
      .filter((s) => String(s.campus_id) === String(id))
      .map((s) => s.id);
    const filteredClasses = allClasses.filter(
      (c) => String(c.campus_id) !== String(id) && !sessionIds.includes(c.session_id),
    );
    saveData(STORAGE_KEYS.CLASSES, filteredClasses);

    showToast("success", "Campus Deleted", "Campus removed successfully.");
    setCampuses((prev) => prev.filter((c) => String(c.id) !== String(id)));
  };

  const handleEditCampus = (campus) => {
    setEditingCampus(campus);
    setCampusForm({
      name: campus.name || "",
      location: campus.location || "",
      principal: campus.principal || "",
    });
  };

  const handleUpdateCampus = async (e) => {
    e.preventDefault();
    if (!editingCampus) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/campuses/${editingCampus.id}`, {
        method: "PUT",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify({
          name: campusForm.name,
          location: campusForm.location || null,
          principal: campusForm.principal || null,
        }),
      });

      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) throw new Error("Failed to update campus");

      const updatedCampus = await res.json();
      showToast(
        "success",
        "Campus Updated",
        `${updatedCampus.name} details have been updated.`,
      );
      setEditingCampus(null);
      setCampusForm({ name: "", location: "", principal: "" });
      setCampuses((prev) =>
        prev.map((campus) =>
          String(campus.id) === String(updatedCampus.id) ? updatedCampus : campus,
        ),
      );
    } catch (error) {
      console.error("Failed to update campus", error);
      showToast("error", "Update Error", "Could not update campus.");
    }
  };

  const handleViewAnalytics = (campus) => {
    const dashboardUrl = getPowerBiDashboardForCampus(campus.name);

    if (!dashboardUrl) {
      showToast(
        "error",
        "Dashboard Not Found",
        `No Power BI dashboard is configured for ${campus.name}.`,
      );
      return;
    }

    setAnalyticsCampus(campus);
    setAnalyticsDashboardUrl(buildPowerBiDashboardUrl(dashboardUrl));
  };

  const handleCloseAnalytics = () => {
    setAnalyticsCampus(null);
    setAnalyticsDashboardUrl("");
  };

  const handleCloseForm = () => {
    setShowAddModal(false);
    setEditingCampus(null);
    setCampusForm({ name: "", location: "", principal: "" });
  };

  const campusesWithDashboardStatus = useMemo(
    () =>
      campuses.map((campus) => ({
        ...campus,
        hasDashboard: Boolean(getPowerBiDashboardForCampus(campus.name)),
      })),
    [campuses],
  );

  const filteredCampuses = useMemo(() => {
    const normalizedSearch = campusSearch.trim().toLowerCase();
    return campusesWithDashboardStatus.filter((campus) =>
      normalizedSearch ? campus.name?.toLowerCase().includes(normalizedSearch) : true,
    );
  }, [campusSearch, campusesWithDashboardStatus]);

  const readyDashboardCount = campusesWithDashboardStatus.filter(
    (campus) => campus.hasDashboard,
  ).length;

  const resetViewerFilters = () => {
    setCampusSearch("");
    setSelectedCampusId("");
  };

  if (analyticsOnly) {
    return (
      <div className="dashboard-content viewer-analytics-page">
        <section className="viewer-analytics-hero">
          <div>
            <span className="viewer-analytics-kicker">Viewer Analytics</span>
            <h2>Campus Power BI Dashboards</h2>
            <p>
              Search campuses, choose a branch, and open the live analytics report without leaving this page.
            </p>
          </div>
          <div className="viewer-analytics-metrics">
            <div className="viewer-analytics-metric">
              <strong className="campus-mgmt-stat-value">{campuses.length}</strong>
              <span>Total Campuses</span>
            </div>
            <div className="viewer-analytics-metric">
            <strong className="campus-mgmt-stat-value">{readyDashboardCount}</strong>
              <span>Dashboards Ready</span>
            </div>
            <div className="viewer-analytics-metric muted">
              <strong className="campus-mgmt-stat-value">{filteredCampuses.length}</strong>
              <span>Showing</span>
            </div>
          </div>
        </section>

        <section className="viewer-campus-toolbar">
          <div className="viewer-filter-field viewer-search-filter">
            <label htmlFor="viewer-campus-search">Search by name</label>
            <div className="viewer-campus-search">
              <MdSearch />
              <input
                id="viewer-campus-search"
                type="search"
                value={campusSearch}
                onChange={(e) => setCampusSearch(e.target.value)}
                placeholder="Search campus name"
              />
            </div>
            {(campusSearch || selectedCampusId) && (
              <button
                type="button"
                className="clear-filter-btn"
                onClick={resetViewerFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        </section>

        <div className="viewer-results-row">
          <span>
            Showing {filteredCampuses.length} of {campuses.length} campus
            {campuses.length === 1 ? "" : "es"}
          </span>
        </div>

        <CampusList
          campuses={filteredCampuses}
          onEdit={handleEditCampus}
          onDelete={handleDeleteCampus}
          onViewAnalytics={handleViewAnalytics}
          onSelectCampus={handleOpenCampusDetail}
          onViewStaff={handleOpenCampusStaff}
          analyticsOnly
        />

        {filteredCampuses.length === 0 && (
          <div className="viewer-empty-state">
            <h3>No campuses found</h3>
            <p>Try another campus name or clear the selected campus filter.</p>
          </div>
        )}

        <CampusAnalyticsModal
          isOpen={!!analyticsCampus}
          campus={analyticsCampus}
          dashboardUrl={analyticsDashboardUrl}
          onClose={handleCloseAnalytics}
          enableDownloadReport
        />
      </div>
    );
  }

  if (isCampusOperator && !isSuperadmin) {
    return (
      <div className="dashboard-content campus-management-page">
        <CampusList
          campuses={filteredCampuses}
          onEdit={handleEditCampus}
          onDelete={handleDeleteCampus}
          onViewAnalytics={handleViewAnalytics}
          onSelectCampus={handleOpenCampusDetail}
          onViewStaff={handleOpenCampusStaff}
          analyticsOnly={false}
          showListHeader={false}
        />

        {filteredCampuses.length === 0 && (
          <div className="viewer-empty-state">
            <h3>No campuses found</h3>
            <p>Try another campus name or clear the search.</p>
          </div>
        )}

        <CampusAnalyticsModal
          isOpen={!!analyticsCampus}
          campus={analyticsCampus}
          dashboardUrl={analyticsDashboardUrl}
          onClose={handleCloseAnalytics}
          enableDownloadReport
        />
      </div>
    );
  }

  return (
    <div className="dashboard-content campus-management-page">
      <section className="campus-management-hero">
        <div className="campus-management-left">
          <div className="viewer-filter-field viewer-search-filter">
            <label htmlFor="superadmin-campus-search">Search by Name</label>
            <div className="viewer-campus-search">
              <MdSearch />
              <input
                id="superadmin-campus-search"
                type="search"
                value={campusSearch}
                onChange={(e) => setCampusSearch(e.target.value)}
                placeholder="Search campus name..."
              />
            </div>
          </div>
        </div>

        <div className="campus-management-right">
          <div className="campus-management-metrics">
            <div className="campus-management-metric">
              <strong className="campus-stat-value">
  {campuses.length}
</strong>
              <span>Total Campuses</span>
            </div>

            <div className="campus-management-metric">
              <strong className="campus-stat-value">
  {readyDashboardCount}
</strong>
              <span>Dashboards Ready</span>
            </div>

            <div className="campus-management-metric muted">
             <strong className="campus-stat-value">
  {filteredCampuses.length}
</strong>
              <span>Showing</span>
            </div>
          </div>
        </div>
      </section>

      <div className="add-campus-btn-wrapper">
        <button
          className="btn-primary add-campus-btn"
          onClick={() => setShowAddModal(true)}
        >
          <MdPersonAdd />
          Add Campus
        </button>
      </div>

      <div className="viewer-results-row campus-management-results">
        {campusSearch && (
          <button type="button" onClick={resetViewerFilters}>
            Clear Filters
          </button>
        )}
      </div>
      

      <CampusList
        campuses={filteredCampuses}
        onEdit={handleEditCampus}
        onDelete={handleDeleteCampus}
        onViewAnalytics={handleViewAnalytics}
        onSelectCampus={handleOpenCampusDetail}
        onViewStaff={handleOpenCampusStaff}
        analyticsOnly={false}
        showListHeader={false}
      />

      {filteredCampuses.length === 0 && (
        <div className="viewer-empty-state">
          <h3>No campuses found</h3>
          <p>Try another campus name or clear the search.</p>
        </div>
      )}

      <CampusFormModal
        isOpen={showAddModal || !!editingCampus}
        isEdit={!!editingCampus}
        formData={campusForm}
        principals={principals}
        onFormChange={handleFormChange}
        onSubmit={editingCampus ? handleUpdateCampus : handleCreateCampus}
        onClose={handleCloseForm}
      />

      <CampusAnalyticsModal
        isOpen={!!analyticsCampus}
        campus={analyticsCampus}
        dashboardUrl={analyticsDashboardUrl}
        onClose={handleCloseAnalytics}
        enableDownloadReport
      />
    </div>
  );
}

export default CampusListView;